import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Brain, 
  ThumbsUp, 
  ThumbsDown, 
  Eye, 
  Share2, 
  MessageCircle,
  X,
  TrendingUp,
  Target,
  Settings
} from 'lucide-react';
import { useSmartRecommendations } from '@/hooks/useSmartRecommendations';
import { useToast } from '@/hooks/use-toast';

interface RecommendedContentProps {
  content: {
    id: string;
    text: string;
    author: string;
    platform: string;
    topics?: string[];
    sentiment?: string;
    recommendation_score: number;
    recommendation_reason: string;
  };
  onInteraction: (type: string) => void;
  onDismiss: () => void;
}

const RecommendedContent: React.FC<RecommendedContentProps> = ({ 
  content, 
  onInteraction, 
  onDismiss 
}) => {
  const getSentimentColor = (sentiment?: string) => {
    switch (sentiment) {
      case 'positive': return 'text-green-600';
      case 'negative': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getPlatformIcon = (platform: string) => {
    // Return appropriate icon based on platform
    return '📱'; // Default icon
  };

  return (
    <Card className="relative">
      <Button
        variant="ghost"
        size="sm"
        className="absolute top-2 right-2 h-6 w-6 p-0"
        onClick={onDismiss}
      >
        <X className="h-4 w-4" />
      </Button>
      
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">{getPlatformIcon(content.platform)}</span>
            <div>
              <CardTitle className="text-sm">@{content.author}</CardTitle>
              <CardDescription className="text-xs">
                {content.platform} • Score: {(content.recommendation_score * 100).toFixed(0)}%
              </CardDescription>
            </div>
          </div>
          <div className="text-right">
            <Progress value={content.recommendation_score * 100} className="w-16 h-2" />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-sm leading-relaxed">{content.text}</p>
        
        {content.topics && content.topics.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {content.topics.slice(0, 3).map((topic, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {topic}
              </Badge>
            ))}
          </div>
        )}

        {content.sentiment && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Sentiment:</span>
            <Badge 
              variant="outline" 
              className={`text-xs ${getSentimentColor(content.sentiment)}`}
            >
              {content.sentiment}
            </Badge>
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          <Target className="inline h-3 w-3 mr-1" />
          {content.recommendation_reason}
        </div>

        <Separator />

        <div className="flex justify-between">
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onInteraction('like')}
              className="h-8 px-2"
            >
              <ThumbsUp className="h-3 w-3 mr-1" />
              Like
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onInteraction('share')}
              className="h-8 px-2"
            >
              <Share2 className="h-3 w-3 mr-1" />
              Share
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onInteraction('comment')}
              className="h-8 px-2"
            >
              <MessageCircle className="h-3 w-3 mr-1" />
              Comment
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onInteraction('hide')}
            className="h-8 px-2 text-muted-foreground"
          >
            <ThumbsDown className="h-3 w-3 mr-1" />
            Hide
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

interface SmartRecommendationsPanelProps {
  contentPool?: Array<{
    id: string;
    text: string;
    author: string;
    platform: string;
    topics?: string[];
    sentiment?: string;
    created_at: string;
  }>;
}

export const SmartRecommendationsPanel: React.FC<SmartRecommendationsPanelProps> = ({ 
  contentPool = [] 
}) => {
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const { toast } = useToast();
  
  const {
    generateRecommendations,
    trackInteraction,
    getRecommendationStats,
    markRecommendationShown,
    dismissRecommendation,
    loading,
    error
  } = useSmartRecommendations();

  useEffect(() => {
    loadRecommendations();
    loadStats();
  }, []);

  const loadRecommendations = async () => {
    if (contentPool.length === 0) {
      // Use sample content for demo
      const sampleContent = [
        {
          id: '1',
          text: 'Just discovered this amazing new AI tool that helps with content creation! The future is here 🚀',
          author: 'tech_guru',
          platform: 'twitter',
          topics: ['AI', 'technology', 'content creation'],
          sentiment: 'positive',
          created_at: new Date().toISOString()
        },
        {
          id: '2',
          text: 'Beautiful sunset from my morning run. Sometimes the simple things in life are the most rewarding.',
          author: 'nature_lover',
          platform: 'instagram',
          topics: ['nature', 'wellness', 'lifestyle'],
          sentiment: 'positive',
          created_at: new Date(Date.now() - 3600000).toISOString()
        },
        {
          id: '3',
          text: 'New research shows the importance of digital wellness in our daily lives. Taking breaks from screens is crucial.',
          author: 'wellness_expert',
          platform: 'linkedin',
          topics: ['wellness', 'productivity', 'mental health'],
          sentiment: 'neutral',
          created_at: new Date(Date.now() - 7200000).toISOString()
        }
      ];
      
      const results = await generateRecommendations(sampleContent, 5);
      setRecommendations(results);
    } else {
      const results = await generateRecommendations(contentPool, 5);
      setRecommendations(results);
    }
  };

  const loadStats = async () => {
    const statsData = await getRecommendationStats();
    setStats(statsData);
  };

  const handleInteraction = async (contentId: string, interactionType: string) => {
    const content = recommendations.find(r => r.id === contentId);
    if (!content) return;

    await trackInteraction({
      content_id: contentId,
      platform: content.platform,
      interaction_type: interactionType as any,
      content_text: content.text,
      content_author: content.author,
      content_topics: content.topics,
      content_sentiment: content.sentiment
    });

    toast({
      title: 'Interaction Recorded',
      description: `Your ${interactionType} has been recorded to improve future recommendations.`,
    });

    // Update recommendation learning
    loadStats();
  };

  const handleDismiss = async (contentId: string) => {
    const recommendationId = contentId; // In real app, this would be the recommendation ID
    await dismissRecommendation(recommendationId);
    
    setRecommendations(prev => prev.filter(r => r.id !== contentId));
    
    toast({
      title: 'Recommendation Dismissed',
      description: 'This content has been removed from your recommendations.',
    });
  };

  const refreshRecommendations = () => {
    loadRecommendations();
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6 text-primary" />
            Smart Recommendations
          </h2>
          <p className="text-muted-foreground">
            AI-powered content suggestions tailored to your interests
          </p>
        </div>
        <Button onClick={refreshRecommendations} disabled={loading}>
          <TrendingUp className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {stats && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recommendation Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{stats.total_shown}</div>
                <div className="text-sm text-muted-foreground">Shown</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{stats.total_clicked}</div>
                <div className="text-sm text-muted-foreground">Clicked</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {(stats.click_through_rate * 100).toFixed(1)}%
                </div>
                <div className="text-sm text-muted-foreground">Click Rate</div>
              </div>
            </div>
            
            {stats.top_topics?.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium mb-2">Your Top Interests:</h4>
                <div className="flex flex-wrap gap-2">
                  {stats.top_topics.map((topic: string, index: number) => (
                    <Badge key={index} variant="secondary">
                      {topic}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <Brain className="h-8 w-8 animate-pulse mx-auto mb-2" />
            <p className="text-muted-foreground">Generating personalized recommendations...</p>
          </div>
        ) : recommendations.length > 0 ? (
          recommendations.map((content) => (
            <RecommendedContent
              key={content.id}
              content={content}
              onInteraction={(type) => handleInteraction(content.id, type)}
              onDismiss={() => handleDismiss(content.id)}
            />
          ))
        ) : (
          <Card>
            <CardContent className="text-center py-8">
              <Target className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No Recommendations Yet</h3>
              <p className="text-muted-foreground mb-4">
                Start interacting with content to get personalized recommendations
              </p>
              <Button onClick={loadRecommendations}>
                Generate Sample Recommendations
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};